import { Injectable } from '@angular/core';
import { DockingService } from '../../../../client/app/docking/docking.service';
import { ErrorLogsService } from '../../../../client/app/error-logs/error-logs.service';
import { EventLogsService } from '../../../../client/app/event-logs/event-logs.service';
import { FlightsService } from '../../../../client/app/flights/flights.service';
import { LocationContextService } from '../../../../client/app/location-context/location-context.service';
import { MaintenanceService } from '../../../../client/app/maintenance/maintenance.service';
import { GeneralMessageService } from '../../../../client/app/shared/general-message/general-message.service';
import { StatisticsService } from '../../../../client/app/statistics/statistics.service';
import { TimeDisplayService } from '../../../../client/app/time-display/time-display.service';
import { IDockingContext } from '../../../../model/docking';
import { IErrorLog } from '../../../../model/error-logs';
import { IEventLog } from '../../../../model/event-logs';
import { IFlight } from '../../../../model/flights';
import { IGeneralMessage } from '../../../../model/general-message';
import {
    IMaintenanceComponent,
    IMaintenanceContext,
    IMaintenanceFailedTest,
    IMaintenanceLink,
    IMaintenanceModeContext,
    IMaintenanceScheduleItem,
    IMaintenanceTaskCompleted
} from '../../../../model/maintenance';
import { IStatistic } from '../../../../model/statistics';
import { ITime } from '../../../../model/time';
import { IWebSocketMessage, WebSocketMessageType } from '../../../../model/web-socket-message';
import { ConnectionStatus } from './../../../../model/connection-status';
import { EnvService } from './../../configuration/env.service';
import { ServerMonitorService } from './server-monitor.service';
import { LicenseService } from '../../license/license.service';
import { AuthenticationService } from '../../authentication/authentication.service';

@Injectable({
    providedIn: 'root'
})
export class WebSocketMessageService {
    constructor(
        private readonly generalMessageService: GeneralMessageService,
        private readonly timeDisplayService: TimeDisplayService,
        private readonly locationContextService: LocationContextService,
        private readonly dockingService: DockingService,
        private readonly flightsService: FlightsService,
        private readonly maintenanceService: MaintenanceService,
        private readonly errorLogsService: ErrorLogsService,
        private readonly eventLogsService: EventLogsService,
        private readonly statisticsService: StatisticsService,
        private readonly serverMonitorService: ServerMonitorService,
        private readonly licenseService: LicenseService,
        private readonly authenticationService: AuthenticationService,
        private readonly env: EnvService
    ) {}

    processMessage(message: IWebSocketMessage) {
        switch (message.headers.response) {
            // Backend Connection
            case WebSocketMessageType.BackendConnectionStatusChange: {
                const connected = message.payload as boolean;
                if (connected) {
                    this.serverMonitorService.updateConnectionStatus(ConnectionStatus.Online);
                } else {
                    this.serverMonitorService.updateConnectionStatus(ConnectionStatus.Offline);
                }
                break;
            }

            // Error handling
            case WebSocketMessageType.ErrorHandling: {
                const errorMessage = `${message?.payload?.code} - ${message?.payload?.message}`;
                this.generalMessageService.showErrorSnackBar({ title: 'Error', message: errorMessage });
                if (this.env.config.errorLog.logErorrsToConsole) {
                    //eslint-disable-next-line no-console
                    console.error(errorMessage);
                }
                break;
            }

            // Time
            case WebSocketMessageType.Time: {
                this.timeDisplayService.loadTimeDisplay(message.payload as ITime);
                break;
            }

            // General
            case WebSocketMessageType.GeneralMessage: {
                this.generalMessageService.showDialog(message.payload.generalMessage as IGeneralMessage);
                break;
            }

            // Location Context
            case WebSocketMessageType.Terminals: {
                this.locationContextService.loadTerminals(message.payload as string[]);
                break;
            }
            case WebSocketMessageType.CenterlineGroups: {
                this.locationContextService.loadCenterlineGroups(message.payload as string[]);
                break;
            }
            case WebSocketMessageType.Centerlines: {
                this.locationContextService.loadCenterlines(message.payload as string[]);
                break;
            }

            // Flights
            case WebSocketMessageType.Flights: {
                this.flightsService.loadFlights(message.payload as IFlight[]);
                break;
            }

            // Docking
            case WebSocketMessageType.Docking: {
                this.dockingService.loadDockingContext(message.payload as IDockingContext);
                break;
            }
            case WebSocketMessageType.Command: {
                this.dockingService.loadDockingContext(message.payload.dockingContext as IDockingContext);
                break;
            }
            case WebSocketMessageType.ResponseHeartbeat: {
                this.dockingService.processHeartBeatResponse(message.payload);
                break;
            }

            // Error Logs
            case WebSocketMessageType.ErrorLogs: {
                this.errorLogsService.loadErrorLogs(message.payload.errorLogs as IErrorLog[]);
                break;
            }

            // Event Logs
            case WebSocketMessageType.EventLogs: {
                this.eventLogsService.loadEventLogs(message.payload.eventLogs as IEventLog[]);
                break;
            }

            // Statistics
            case WebSocketMessageType.Statistics: {
                this.statisticsService.loadStatistics(message.payload.statistics as IStatistic[]);
                break;
            }

            // Maintenance
            case WebSocketMessageType.MaintenanceContext: {
                this.maintenanceService.loadMaintenanceContext(message.payload as IMaintenanceContext);
                break;
            }
            case WebSocketMessageType.MaintenanceTestCommand: {
                this.maintenanceService.loadMaintenanceContext(
                    message.payload.maintenanceContext as IMaintenanceContext
                );
                break;
            }
            case WebSocketMessageType.MaintenanceModeContext: {
                this.maintenanceService.loadMaintenanceModeContext(
                    message.payload.maintenanceModeContext as IMaintenanceModeContext
                );
                break;
            }
            case WebSocketMessageType.MaintenanceLinks: {
                this.maintenanceService.loadMaintenanceLinks(message.payload.maintenanceLinks as IMaintenanceLink[]);
                break;
            }
            case WebSocketMessageType.MaintenanceCommands: {
                this.maintenanceService.loadMaintenanceCommands(message.payload.maintenanceCommands as string[]);
                break;
            }
            case WebSocketMessageType.MaintenanceFailedTests: {
                this.maintenanceService.loadMaintenanceFailedTests(
                    message.payload.maintenanceFailedTests as IMaintenanceFailedTest[]
                );
                break;
            }
            case WebSocketMessageType.MaintenanceComponents: {
                this.maintenanceService.loadMaintenanceComponents(
                    message.payload.maintenanceComponents as IMaintenanceComponent[]
                );
                break;
            }
            case WebSocketMessageType.MaintenanceSchedule: {
                this.maintenanceService.loadMaintenanceSchedule(
                    message.payload.maintenanceSchedule as IMaintenanceScheduleItem[]
                );
                break;
            }
            case WebSocketMessageType.MaintenanceTasksCompleted: {
                this.maintenanceService.loadMaintenanceTasksCompleted(
                    message.payload.maintenanceTasksCompleted as IMaintenanceTaskCompleted[]
                );
                break;
            }
            case WebSocketMessageType.License: {
                this.licenseService.setLicenseState(message.payload);
                break;
            }
            case WebSocketMessageType.LicenseUsage: {
                this.licenseService.setLicenseUsage(message.payload);
                break;
            }

            case WebSocketMessageType.SiemEvent: {
                this.authenticationService.checkValidToken(message.payload);
            }
        }
    }
}
